// =============================================================================
// ModifierPanel — Campaign Combat Modifiers (Campaign tab + Routines tab embed)
// No browser alert() calls — all feedback is inline via the banner state.
// =============================================================================
import { Fragment, useEffect, useState } from 'react';
import { Plus, Shield, Loader2, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import type { CombatModifierWithStates } from '../../../../backend/shared/types/index';
import ModifierCard from './ModifierCard';
import ModifierForm, { type ModifierFormData } from './ModifierForm';

interface BonusTypeOption { typeId: string; name: string; stackingRule: string; isBuiltIn: boolean }
interface CharacterRow { characterId: string; name: string }

interface Props {
  campaignId:          string;
  characters:          CharacterRow[];
  isOwnerOrAdmin:      boolean;
  /** The current user's ID — lets modifier creators edit/delete their own modifiers. */
  currentUserId?:      string;
  /** When true renders without section heading — for embedding inside RoutinesTab. */
  compact?:            boolean;
  /** Default ownerType for new modifiers ('world' for campaign tab, 'character' for char sheet). */
  defaultOwnerType?:   'character' | 'world';
  /** Character ID to pre-fill for character-type modifiers created from the char sheet. */
  ownerCharacterId?:   string;
  ownerCharacterName?: string;
  gameSystem?:         import('../../../../backend/shared/types/index').RuleSystem;
  /** Called with updated modifier list so parent can refresh (e.g. executor bonuses). */
  onModifiersChange?:  (mods: CombatModifierWithStates[]) => void;
}

type FormMode = { type: 'create' } | { type: 'edit'; modifier: CombatModifierWithStates } | null;
type Banner   = { ok: boolean; msg: string } | null;

export default function ModifierPanel({
  campaignId, characters, isOwnerOrAdmin, currentUserId, compact, defaultOwnerType, ownerCharacterId, ownerCharacterName, gameSystem, onModifiersChange,
}: Props) {
  const [modifiers,  setModifiers]  = useState<CombatModifierWithStates[]>([]);
  const [bonusTypes, setBonusTypes] = useState<BonusTypeOption[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [formMode,   setFormMode]   = useState<FormMode>(null);
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState<string | null>(null);
  const [banner,     setBanner]     = useState<Banner>(null);

  const bonusTypeNames = Object.fromEntries(bonusTypes.map((t) => [t.typeId, t.name]));

  function showBanner(msg: string, ok = true) {
    setBanner({ ok, msg });
    setTimeout(() => setBanner(null), 3500);
  }

  function sync(next: CombatModifierWithStates[]) {
    setModifiers(next);
    onModifiersChange?.(next);
  }

  useEffect(() => {
    Promise.all([
      apiFetch<{ modifiers: CombatModifierWithStates[] }>(`/campaigns/${campaignId}/combat/modifiers`),
      apiFetch<{ bonusTypes: BonusTypeOption[] }>(`/campaigns/${campaignId}/combat/bonus-types`),
    ])
      .then(([mods, types]) => { sync(mods.modifiers); setBonusTypes(types.bonusTypes); })
      .catch((e) => setError(e.message ?? 'Failed to load modifiers'))
      .finally(() => setLoading(false));
  }, [campaignId]);

  async function handleToggleActive(modifierId: string, isActive: boolean) {
    // Optimistic update — PATCH returns { modifier: ... } not a flat CombatModifierWithStates,
    // so reading the response would corrupt state and throw when effects.map() is called.
    sync(modifiers.map((m) => m.modifierId === modifierId ? { ...m, isActive } : m));
    try {
      await apiFetch(
        `/campaigns/${campaignId}/combat/modifiers/${modifierId}`,
        { method: 'PATCH', body: JSON.stringify({ isActive }) },
      );
    } catch (e: any) {
      // Revert on failure
      sync(modifiers.map((m) => m.modifierId === modifierId ? { ...m, isActive: !isActive } : m));
      showBanner(e.message ?? 'Failed to update modifier', false);
    }
  }

  async function handleToggleCharacter(modifierId: string, charId: string, enabled: boolean) {
    try {
      await apiFetch(
        `/campaigns/${campaignId}/combat/modifiers/${modifierId}/characters/${charId}`,
        { method: 'PUT', body: JSON.stringify({ enabled }) },
      );
      sync(modifiers.map((m) =>
        m.modifierId === modifierId
          ? { ...m, characterStates: { ...m.characterStates, [charId]: enabled } }
          : m,
      ));
    } catch (e: any) { showBanner(e.message ?? 'Failed to update', false); }
  }

  async function handleSave(data: ModifierFormData) {
    setSaving(true);
    try {
      if (formMode?.type === 'edit') {
        const res = await apiFetch<{ modifier: CombatModifierWithStates }>(
          `/campaigns/${campaignId}/combat/modifiers/${formMode.modifier.modifierId}`,
          { method: 'PATCH', body: JSON.stringify(data) },
        );
        sync(modifiers.map((m) =>
          m.modifierId === formMode.modifier.modifierId
            ? { ...res.modifier, characterStates: m.characterStates }
            : m,
        ));
        showBanner('Modifier updated.');
      } else {
        const res = await apiFetch<{ modifier: CombatModifierWithStates }>(
          `/campaigns/${campaignId}/combat/modifiers`,
          { method: 'POST', body: JSON.stringify(data) },
        );
        sync([...modifiers, res.modifier]);
        showBanner('Modifier added.');
      }
      setFormMode(null);
    } catch (e: any) { showBanner(e.message ?? 'Failed to save modifier', false); }
    finally { setSaving(false); }
  }

  async function handleDelete(modifierId: string): Promise<void> {
    setDeleteId(modifierId);
    try {
      await apiFetch(`/campaigns/${campaignId}/combat/modifiers/${modifierId}`, { method: 'DELETE' });
      sync(modifiers.filter((m) => m.modifierId !== modifierId));
    } catch (e: any) { showBanner(e.message ?? 'Failed to delete modifier', false); }
    finally { setDeleteId(null); }
  }

  if (loading) return (
    <div className="flex items-center gap-2 text-parchment-muted/50 py-4 justify-center text-xs">
      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading modifiers…
    </div>
  );
  if (error) return (
    <p className="text-danger text-xs px-1">{error}</p>
  );

  return (
    <div className="space-y-3">
      {/* Inline banner (no alert()) */}
      {banner && (
        <div className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-xs ${
          banner.ok
            ? 'bg-success/10 text-success border border-success/20'
            : 'bg-danger/10 text-danger border border-danger/20'
        }`}>
          <span>{banner.msg}</span>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Header — suppressed in compact (embedded) mode */}
      {!compact && (
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-base text-parchment">Combat Modifiers</h2>
            <p className="text-xs text-parchment-muted/50 mt-0.5">
              Party-wide buffs and debuffs that affect character rolls.
            </p>
          </div>
          {!formMode && (
            <button onClick={() => setFormMode({ type: 'create' })}
              className="btn-primary flex items-center gap-1.5 text-xs shrink-0">
              <Plus className="w-3.5 h-3.5" /> Add Modifier
            </button>
          )}
        </div>
      )}

      {/* Inline Add button in compact mode */}
      {compact && !formMode && (
        <button onClick={() => setFormMode({ type: 'create' })}
          className="w-full flex items-center justify-center gap-1.5 py-2
                     border border-dashed border-darkbg-border rounded-lg
                     text-xs text-parchment-muted/40 hover:text-gold hover:border-gold/40 transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Modifier
        </button>
      )}

      {/* Create form. Edit forms render beside their selected card below so
          clicking a lower row never opens an off-screen editor. */}
      {formMode?.type === 'create' && (
        <div className="bg-darkbg-card border border-gold/30 rounded-lg p-4">
          <h3 className="font-display text-sm text-gold mb-3">
            New Modifier
          </h3>
          <ModifierForm
            bonusTypes={bonusTypes}
            gameSystem={gameSystem}
            saving={saving}
            canSetOwnerType={isOwnerOrAdmin}
            defaultOwnerType={defaultOwnerType ?? 'world'}
            defaultOwnerCharId={ownerCharacterId}
            defaultOwnerCharName={ownerCharacterName}
            onSave={handleSave}
            onCancel={() => setFormMode(null)}
          />
        </div>
      )}

      {/* Modifier list */}
      {modifiers.length === 0 && !formMode && (
        <div className={`text-center py-6 space-y-1 ${compact ? '' : 'card'}`}>
          <Shield className="w-6 h-6 mx-auto text-parchment-muted/20" />
          <p className="text-parchment-muted/50 text-xs">No modifiers yet.</p>
        </div>
      )}
      {modifiers.length > 0 && (
        <div className="space-y-2">
          {modifiers.map((m) => (
            <Fragment key={m.modifierId}>
              <ModifierCard
                modifier={m}
                bonusTypeNames={bonusTypeNames}
                characters={characters}
                isOwnerOrAdmin={isOwnerOrAdmin && deleteId !== m.modifierId}
                currentUserId={currentUserId}
                onToggleActive={handleToggleActive}
                onToggleCharacter={handleToggleCharacter}
                onEdit={(mod) => setFormMode({ type: 'edit', modifier: mod })}
                onDelete={handleDelete}
              />
              {formMode?.type === 'edit' && formMode.modifier.modifierId === m.modifierId && (
                <div className="bg-darkbg-card border border-gold/30 rounded-lg p-4">
                  <h3 className="font-display text-sm text-gold mb-3">Edit Modifier</h3>
                  <ModifierForm
                    initial={formMode.modifier}
                    bonusTypes={bonusTypes}
                    gameSystem={gameSystem}
                    saving={saving}
                    canSetOwnerType={isOwnerOrAdmin}
                    defaultOwnerType={defaultOwnerType ?? 'world'}
                    defaultOwnerCharId={ownerCharacterId}
                    defaultOwnerCharName={ownerCharacterName}
                    onSave={handleSave}
                    onCancel={() => setFormMode(null)}
                  />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
